import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { LoadLine, EASE } from "./Reveal";
import BarrierScene from "./BarrierScene";
import { useLang } from "../i18n";
import { trackGA4Event } from "../analytics/ga4";

const meta = [
    { en: "Gate Openers", hi: "गेट ओपनर" },
    { en: "Door Openers", hi: "डोर ओपनर" },
    { en: "Pedestrian Openers", hi: "पैदल ओपनर" },
    { en: "Parking Barriers", hi: "पार्किंग बैरियर" },
    { en: "Access Control", hi: "एक्सेस कंट्रोल" },
    { en: "Software", hi: "सॉफ्टवेयर" },
];

export default function Hero() {
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 900], [0, 180]);
    const { lang } = useLang();
    const t = (en, hi) => (lang === "hi" ? hi : en);

    return (
        <section data-testid="hero-section-container" className="relative flex min-h-svh flex-col overflow-hidden">
            <motion.div style={{ y }} className="absolute inset-0">
                <div className="absolute inset-0 bg-[#0D0F12]" />
                <BarrierScene />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/55 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/30" />
            </motion.div>

            <div className="relative mx-auto flex w-full max-w-[1500px] flex-1 flex-col justify-end px-5 pb-8 pt-36 md:px-10">
                <LoadLine delay={0.15} className="mb-5">
                    <span className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-brand md:text-[11px]">
                        <span className="h-2 w-2 animate-pulse bg-brand" />
                        {t("Manufactured in Jaipur, Rajasthan", "जयपुर, राजस्थान में निर्मित")}
                    </span>
                </LoadLine>

                {lang === "hi" ? (
                    <h1 className="font-display leading-[1] tracking-tight text-[clamp(2.6rem,7.5vw,7rem)] text-paper">
                        <LoadLine delay={0.28}>गेट खुलते हैं<span className="text-brand">.</span></LoadLine>
                        <LoadLine delay={0.4}>दरवाज़े चलते हैं<span className="text-brand">.</span></LoadLine>
                        <LoadLine delay={0.52}>
                            पहुँच नियंत्रित रहती है<span className="text-brand">.</span>
                        </LoadLine>
                    </h1>
                ) : (
                    <h1 className="font-display uppercase leading-[0.88] tracking-tight text-[clamp(3.2rem,9.5vw,9rem)] text-paper">
                        <LoadLine delay={0.28}>Gates open<span className="text-brand">.</span></LoadLine>
                        <LoadLine delay={0.4}>Doors move<span className="text-brand">.</span></LoadLine>
                        <LoadLine delay={0.52}>
                            Access is<br />controlled<span className="text-brand">.</span>
                        </LoadLine>
                    </h1>
                )}

                <div className="mt-8 flex flex-col gap-8 md:mt-10 md:flex-row md:items-end md:justify-between">
                    <LoadLine delay={0.7} className="max-w-lg">
                        <span className="block text-sm leading-relaxed text-zinc-300 md:text-base">
                            {t(
                                "Desamatic Automation manufactures gate, door and pedestrian entrance automation, parking barriers, and access control hardware — built for continuous daily use at homes, societies, factories and commercial sites.",
                                "Desamatic Automation गेट, दरवाज़े और पैदल प्रवेश ऑटोमेशन, पार्किंग बैरियर और एक्सेस कंट्रोल उपकरण बनाती है — घरों, सोसाइटी, कारखानों और वाणिज्यिक साइटों में रोज़ाना लगातार उपयोग के लिए बने।"
                            )}
                        </span>
                    </LoadLine>
                    <LoadLine delay={0.82}>
                        <span className="flex flex-wrap items-center gap-4">
                            <a
                                data-testid="hero-cta-request-quote"
                                href="#contact"
                                onClick={() => trackGA4Event("generate_lead", { cta_location: "hero" })}
                                className="group inline-flex items-center gap-3 bg-brand px-7 py-4 text-sm font-semibold uppercase tracking-wider text-ink transition-colors duration-300 hover:bg-paper"
                            >
                                {t("Get a quote", "कोटेशन पाएं")}
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                            <a
                                data-testid="hero-cta-explore-products"
                                href="#products"
                                className="inline-flex items-center gap-3 border border-white/25 px-7 py-4 text-sm font-semibold uppercase tracking-wider text-paper transition-colors duration-300 hover:border-brand hover:text-brand"
                            >
                                {t("View products", "उत्पाद देखें")}
                            </a>
                        </span>
                    </LoadLine>
                </div>

                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.2, delay: 1.05, ease: EASE }}
                    style={{ transformOrigin: "left" }}
                    className="hazard-line mt-10 h-1.5 w-full md:mt-12"
                />

                <div className="mt-6 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-white/10 pt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    {meta.map((m, i) => (
                        <span key={m.en} className="flex items-center gap-2">
                            <span className="text-brand">{String(i + 1).padStart(2, "0")}</span>
                            {m[lang]}
                        </span>
                    ))}
                    <span className="flex items-center gap-2">
                        {t("Scroll", "नीचे देखें")} <ArrowDown className="h-3 w-3 animate-bounce" />
                    </span>
                </div>
            </div>
        </section>
    );
}
